import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MapPin, Network as NetworkIcon } from 'lucide-react';
import type { Topology } from '@simpleacs/shared';
import { api } from '../../lib/api';
import { useTasks } from '../../lib/tasks';
import { Button, Field, Pill } from '../../components/ui';
import { WizardDialog } from '../../components/WizardDialog';
import { SplitterSelect, ZoneSelect } from '../../components/topology-select';
import { SplitterPortGrid } from './SplitterPortGrid';

/**
 * Where an ONU physically sits: its zone, the splitter feeding it, and which
 * port of that splitter.
 *
 * Offered from the ONU list rather than only the detail page, because the
 * moment somebody notices a subscriber has no placement is while scanning the
 * list for the ones that do not. Making them open a second page to fix a blank
 * cell is how the blanks stay.
 *
 * The steps narrow: a zone holds splitters, a splitter holds ports. Choosing a
 * zone first means the splitter list is the handful in that area rather than
 * every splitter the operator owns.
 *
 * Saving closes the dialog at once; the write runs in the tasks tray and the
 * ONU list and topology refresh when it lands.
 */
export function PlacementDialog({
  onuId,
  label,
  currentZoneId,
  currentSplitterId,
  onClose,
}: {
  onuId: string;
  label: string;
  currentZoneId?: string | null;
  currentSplitterId?: string | null;
  onClose: () => void;
}) {
  const tasks = useTasks();
  const [zoneId, setZoneId] = useState(currentZoneId ?? '');
  const [splitterId, setSplitterId] = useState(currentSplitterId ?? '');
  const [port, setPort] = useState('');

  const topology = useQuery({
    queryKey: ['topology'],
    queryFn: () => api.get<Topology>('/topology'),
  });

  const zones = topology.data?.zones ?? [];
  const splitters = useMemo(
    () => (topology.data?.splitters ?? []).filter((s) => !zoneId || s.zoneId === zoneId),
    [topology.data, zoneId],
  );
  const chosen = splitters.find((s) => s.id === splitterId);

  const save = () => {
    // Snapshotted: the dialog is gone by the time the write settles.
    const body = {
      zoneId: zoneId || null,
      splitterId: splitterId || null,
      splitterPort: port === '' ? null : Number(port),
    };
    onClose();
    tasks.start({
      label: `Place ${label}`,
      run: () => api.patch(`/onus/${encodeURIComponent(onuId)}/placement`, body),
      done: () => `${label} placed.`,
      invalidate: [['onus'], ['topology']],
    });
  };

  return (
    <WizardDialog
      title={`Place ${label}`}
      subtitle="Where this ONU physically sits. Recorded here only — the OLT has no knowledge of splitters."
      maxWidth="lg"
      onClose={onClose}
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={save}>
            Save
          </Button>
        </div>
      }
    >
      <Field label="Zone" hint="The service area. Narrows the splitters below.">
            <ZoneSelect
              value={zoneId}
              zones={zones}
              emptyLabel="Not in a zone"
              onChange={(next) => {
                setZoneId(next);
                // A splitter from the old zone would not be in the new list.
                setSplitterId('');
                setPort('');
              }}
            />
          </Field>

          <Field
            label="Splitter"
            hint={
              zoneId
                ? `${splitters.length} in this zone`
                : 'Every splitter — pick a zone to narrow it'
            }
          >
            <SplitterSelect
              value={splitterId}
              splitters={splitters}
              zoneId={zoneId || null}
              emptyLabel="No splitter"
              optionHint={(s) => {
                // The fill, which is what decides whether this is the right
                // box — kept as the row's second line rather than crammed into
                // the name, so a long name still reads.
                const full = splitters.find((x) => x.id === s.id);
                return full ? `1:${full.capacity} · ${full.deviceCount ?? 0} used` : null;
              }}
              onChange={(next) => {
                setSplitterId(next);
                setPort('');
              }}
            />
          </Field>

          {chosen && (
            <Field label="Port" hint="Ports already taken are not offered">
              <SplitterPortGrid
                splitterId={chosen.id}
                capacity={chosen.capacity}
                value={port}
                onChange={setPort}
              />
            </Field>
          )}

          {chosen && (
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <Pill tone="primary">
                <MapPin className="mr-1 inline size-3" />
                {zones.find((z) => z.id === zoneId)?.name ?? 'no zone'}
              </Pill>
              <Pill tone="info">
                <NetworkIcon className="mr-1 inline size-3" />
                {chosen.name}
                {port ? ` port ${port}` : ''}
              </Pill>
            </div>
          )}
    </WizardDialog>
  );
}
